import React from 'react';
import { isFormattedLegalContent, sanitizeLegalHtml } from '../utils/legalContent';

export const LEGAL_RICH_CONTENT_CLASSES = [
  'legal-rich-content text-sm leading-relaxed text-muted-foreground',
  '[&_h2]:mb-3 [&_h2]:mt-7 [&_h2]:font-display [&_h2]:text-2xl [&_h2]:font-bold [&_h2]:text-foreground',
  '[&_h3]:mb-2 [&_h3]:mt-6 [&_h3]:font-display [&_h3]:text-xl [&_h3]:font-semibold [&_h3]:text-foreground',
  '[&_h4]:mb-2 [&_h4]:mt-5 [&_h4]:font-display [&_h4]:text-lg [&_h4]:font-semibold [&_h4]:text-foreground',
  '[&_h5]:mt-4 [&_h5]:font-semibold [&_h5]:text-foreground [&_h6]:mt-4 [&_h6]:font-semibold [&_h6]:text-foreground',
  '[&_p]:my-3 [&_p]:whitespace-pre-wrap',
  '[&_ul]:my-3 [&_ul]:list-disc [&_ul]:space-y-1 [&_ul]:pl-6',
  '[&_ol]:my-3 [&_ol]:list-decimal [&_ol]:space-y-1 [&_ol]:pl-6',
  '[&_blockquote]:my-4 [&_blockquote]:border-l-4 [&_blockquote]:border-primary/30 [&_blockquote]:pl-4 [&_blockquote]:italic',
  '[&_a]:font-medium [&_a]:text-primary [&_a]:underline [&_a]:underline-offset-2',
  '[&_table]:my-5 [&_table]:w-full [&_table]:border-collapse [&_table]:text-left',
  '[&_th]:border [&_th]:border-border [&_th]:bg-secondary/60 [&_th]:p-2 [&_th]:font-semibold [&_th]:text-foreground',
  '[&_td]:border [&_td]:border-border [&_td]:p-2 [&_hr]:my-6 [&_hr]:border-border',
].join(' ');

const headingClasses = {
  1: 'font-display text-2xl md:text-3xl font-bold text-foreground',
  2: 'font-display text-xl md:text-2xl font-semibold text-foreground',
  3: 'font-display text-lg md:text-xl font-semibold text-foreground',
  4: 'font-display text-base md:text-lg font-semibold text-foreground',
  5: 'font-display text-sm md:text-base font-semibold text-foreground',
  6: 'font-display text-sm font-semibold text-foreground',
};

const parseLegalDocumentContent = (content) => {
  const blocks = [];
  let paragraphLines = [];

  const flushParagraph = () => {
    if (paragraphLines.length === 0) return;
    blocks.push({ type: 'paragraph', text: paragraphLines.join('\n') });
    paragraphLines = [];
  };

  String(content || '').split(/\r?\n/).forEach((line) => {
    const headingMatch = line.match(/^(#{1,6})\s*(\S.*)$/);
    if (headingMatch) {
      flushParagraph();
      blocks.push({
        type: 'heading',
        level: headingMatch[1].length,
        text: headingMatch[2].trim(),
      });
      return;
    }

    if (!line.trim()) {
      flushParagraph();
      return;
    }

    paragraphLines.push(line);
  });

  flushParagraph();
  return blocks;
};

export function LegalDocumentContent({ content, className = '' }) {
  if (isFormattedLegalContent(content)) {
    return (
      <div
        className={`${LEGAL_RICH_CONTENT_CLASSES} ${className}`.trim()}
        dangerouslySetInnerHTML={{ __html: sanitizeLegalHtml(content) }}
      />
    );
  }

  const blocks = parseLegalDocumentContent(content);

  return (
    <div className={`space-y-4 ${className}`.trim()}>
      {blocks.map((block, index) => {
        if (block.type === 'heading') {
          const tagName = `h${Math.min(block.level + 1, 6)}`;
          return React.createElement(
            tagName,
            {
              key: `heading-${index}`,
              className: `${headingClasses[block.level]} pt-2 first:pt-0`,
            },
            block.text,
          );
        }

        return (
          <p
            key={`paragraph-${index}`}
            className="whitespace-pre-wrap text-sm leading-relaxed text-muted-foreground"
          >
            {block.text}
          </p>
        );
      })}
    </div>
  );
}
